import type { ChartData, Palace, Star } from "../types";

/** Minimum rendered stars in one palace to consider "dense". */
export const DENSE_PALACE_MIN_STAR_COUNT = 4;

/** Minimum stars with 四化 tags (when activation tags are shown). */
export const DENSE_PALACE_MIN_ACTIVATION_STAR_COUNT = 3;

/** Global palace row / cell height multiplier when any palace is dense. */
export const DENSE_PALACE_HEIGHT_BOOST_RATIO = 1.2;

/**
 * Stars shown in the palace card (matches Palace.buildStarRenderEntries).
 */
export function getPalaceRenderableStars(palace: Palace): Star[] {
  return [...palace.minorStars, ...(palace.mainStar ?? [])];
}

/**
 * Count stars that would show at least one activation tag when tags are enabled.
 */
export function countPalaceActivationStars(palace: Palace): number {
  return getPalaceRenderableStars(palace).filter(
    (star) => (star.transformations?.length ?? 0) > 0
  ).length;
}

/**
 * True when this palace meets the dense-palace rule (4+ stars, 3+ with 四化).
 */
export function palaceQualifiesForDenseHeightBoost(
  palace: Palace,
  activationTagsEnabled: boolean
): boolean {
  const stars = getPalaceRenderableStars(palace);
  if (stars.length < DENSE_PALACE_MIN_STAR_COUNT) {
    return false;
  }
  if (!activationTagsEnabled) {
    return false;
  }
  return (
    countPalaceActivationStars(palace) >=
    DENSE_PALACE_MIN_ACTIVATION_STAR_COUNT
  );
}

/**
 * True when any palace in the chart triggers a global ~20% height boost.
 */
export function chartQualifiesForDensePalaceHeightBoost(
  chartData: ChartData,
  activationTagsEnabled: boolean
): boolean {
  if (!activationTagsEnabled) {
    return false;
  }
  return chartData.palaces.some((palace) =>
    palaceQualifiesForDenseHeightBoost(palace, true)
  );
}
